import { LAUNCH_URL_MAX, LAUNCH_URL_SCHEME, type ArrivalMethod } from "@sagot/constants";

/** Which ways in are open for one agent. */
export type Arrival = Readonly<Record<ArrivalMethod, boolean>>;

export interface AgentArrival {
  agent_id: string;
  name: string;
  live: boolean;
  arrival: Arrival;
}

/** Edits not saved yet, by agent id. */
export type ArrivalDraft = Readonly<Record<string, Arrival>>;

export const ALL_ON: Arrival = { launch: true, email_code: true, open_link: true };

/** Access token, idle timeout and maximum choices; the API accepts any value inside its bounds, the page offers these steps. */
export const ACCESS_MINUTES_CHOICES = [5, 10, 15, 30, 45, 60] as const;
export const IDLE_MINUTES_CHOICES = [5, 10, 15, 30, 45, 60] as const;
export const ABSOLUTE_HOURS_CHOICES = [1, 2, 4, 6, 8, 10, 12] as const;

/** At least one way in has to stay on for every agent (the API refuses a save that turns the last one off). */
export function hasWayIn(arrival: Arrival): boolean {
  return arrival.launch || arrival.email_code || arrival.open_link;
}

export function sameArrival(a: Arrival, b: Arrival): boolean {
  return a.launch === b.launch && a.email_code === b.email_code && a.open_link === b.open_link;
}

/** The arrival the page shows for an agent: the draft, else what is saved. */
export function arrivalOf(agents: readonly AgentArrival[], draft: ArrivalDraft, agentId: string): Arrival {
  return draft[agentId] ?? agents.find((agent) => agent.agent_id === agentId)?.arrival ?? ALL_ON;
}

/** Agents whose draft differs from what is saved, in the API's request shape. */
export function changedAgents(agents: readonly AgentArrival[], draft: ArrivalDraft): { agent_id: string; arrival: Arrival }[] {
  return agents.flatMap((agent) => {
    const edited = draft[agent.agent_id];
    return edited && !sameArrival(edited, agent.arrival) ? [{ agent_id: agent.agent_id, arrival: edited }] : [];
  });
}

/** Every edited agent keeps a way in. */
export function draftIsSavable(draft: ArrivalDraft): boolean {
  return Object.values(draft).every(hasWayIn);
}

export type UrlProblem = "invalid" | "http" | "wildcard" | "credentials" | "fragment" | "tooLong";
export type UrlResult = { ok: true; value: string } | { ok: false; problem: UrlProblem };

const SCHEME = /^[a-z][a-z\d+.-]*:\/\//i;
const HOST = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

function problemOf(value: string): UrlProblem {
  if (value.includes("*")) return "wildcard";
  if (/^http:\/\//i.test(value)) return "http";
  return "invalid";
}

/**
 * One typed address -> the exact https address the API stores, or what is wrong with it. A bare `www.example.com/account` gets
 * `https://`. Wildcards, `http:`, a user name or password and a `#fragment` are refused; the mint compares return URLs exactly.
 */
export function parseHttpsUrl(raw: string): UrlResult {
  let value = raw.trim();
  if (value === "") return { ok: false, problem: "invalid" };
  if (!SCHEME.test(value) && !value.includes("*") && !value.includes("@")) value = `${LAUNCH_URL_SCHEME}${value}`;
  if (value.length > LAUNCH_URL_MAX) return { ok: false, problem: "tooLong" };
  if (value.includes("*") || /^http:\/\//i.test(value)) return { ok: false, problem: problemOf(value) };
  if (!/^https:\/\//i.test(value) || /[\s<>"'`\\]/.test(value)) return { ok: false, problem: "invalid" };
  value = `${LAUNCH_URL_SCHEME}${value.slice(LAUNCH_URL_SCHEME.length)}`;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { ok: false, problem: "invalid" };
  }
  if (url.protocol !== "https:" || !HOST.test(url.hostname)) return { ok: false, problem: "invalid" };
  if (url.username !== "" || url.password !== "") return { ok: false, problem: "credentials" };
  if (url.hash !== "" || value.includes("#")) return { ok: false, problem: "fragment" };
  return { ok: true, value };
}

/** Same list, same order. */
export function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

const CODE_IN_URL = /(#code=lc_)[A-Za-z0-9_-]+/;
export const MASK = "••••••••";

/** The launch URL with the code hidden (what the page shows; the link and the copy button keep the real one). */
export function maskCode(url: string): string {
  return url.replace(CODE_IN_URL, `$1${MASK}`);
}

/** `{ "code": "lc_••••", "tenant": "<slug>" }` from the JSON keys the hosted chat sent; values are placeholders, never the real code. */
export function redeemBodyPreview(keys: readonly string[], slug: string): string {
  const shown: Record<string, string> = {};
  for (const key of keys) shown[key] = key === "code" ? `lc_${MASK}` : key === "tenant" ? slug : "…";
  return JSON.stringify(shown, null, 2);
}

/** Seconds left until `iso` (never negative). */
export function secondsUntil(iso: string, now: number): number {
  return Math.max(Math.ceil((new Date(iso).getTime() - now) / 1000), 0);
}
